import { graphql, useStaticQuery } from 'gatsby';
import gql from 'graphql-tag';
import React, { useMemo, useState } from 'react';
import { useClient, useQuery } from 'urql';

import {
  Card,
  CardBody,
  DateField,
  Form,
  SelectField,
  SelectOption,
  SubmitButton,
} from '@/components';
import { useTranslate } from '@/contexts';
import { BrandEnum } from '@/globalTypes';
import { downloadFile } from '@/utils';
import {
  CashbackReportBoxQuery,
  CashbackReportQuery,
  CashbackReportQueryVariables,
} from './__generated__/component';

const BLOCK_STATIC_QUERY = graphql`
  query SanityCashbackReportBlock {
    sanityCashbackReportBlock {
      title {
        ...SanityLocaleString
      }
      selectBrand {
        ...SanityLocaleString
      }
      brandLabel {
        ...SanityLocaleString
      }
      fromLabel {
        ...SanityLocaleString
      }
      toLabel {
        ...SanityLocaleString
      }
      exportCashback {
        ...SanityLocaleString
      }
    }
  }
`;

const QUERY = gql`
  query CashbackReportBox {
    viewer {
      id
      brands {
        code
        name
      }
    }
  }
`;

const REPORT_QUERY = gql`
  query CashbackReport(
    $brand: BrandEnum!
    $from: OffsetDateTime!
    $to: OffsetDateTime!
  ) {
    viewer {
      id
      exportedCashbackReport: exportedCashbackReportV5(
        brand: $brand
        from: $from
        to: $to
      )
    }
  }
`;

type FormValues = {
  brand: BrandEnum | '';
  from: string;
  to: string;
};

const CashbackReportBlock = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const { t } = useTranslate();
  const client = useClient();
  const [{ data }] = useQuery<CashbackReportBoxQuery>({
    query: QUERY,
  });

  const block =
    useStaticQuery<Queries.SanityCashbackReportBlockQuery>(
      BLOCK_STATIC_QUERY,
    ).sanityCashbackReportBlock;

  const today = useMemo(() => new Date(), []);

  const brandOptions = useMemo(() => {
    const brandOptions: SelectOption[] =
      data?.viewer.brands.map((brand) => ({
        label: brand.name,
        value: brand.code,
      })) ?? [];

    const options: SelectOption[] = [
      {
        label: t(block?.selectBrand),
        value: '',
      },
      ...brandOptions,
    ];

    return options;
  }, [data, block?.selectBrand, t]);

  const defaultValues: FormValues = {
    brand: '',
    from: '',
    to: '',
  };

  const onSubmit = ({ brand, from, to }: FormValues) => {
    if (!brand) {
      return;
    }

    setIsGenerating(true);

    client
      .query<CashbackReportQuery, CashbackReportQueryVariables>(
        REPORT_QUERY,
        {
          brand,
          from,
          to,
        },
        {
          requestPolicy: 'network-only',
        },
      )
      .toPromise()
      .then(async (res) => {
        const url = res.data?.viewer.exportedCashbackReport;

        if (url) {
          await downloadFile(url, {
            fileName: 'cashback-report.csv',
          });
        }
      })
      .finally(() => {
        setIsGenerating(false);
      });
  };

  if (!block) {
    return null;
  }

  return (
    <Card size="lg" title={t(block.title)}>
      <CardBody>
        <div className="p-3">
          <Form
            defaultValues={defaultValues}
            onSubmit={onSubmit}
            className="grid gap-3"
          >
            <SelectField
              name="brand"
              id="cashback-report-block__brand"
              title={t(block.brandLabel)}
              options={brandOptions}
              required
            />
            <DateField
              name="from"
              id="cashback-report-block__from"
              title={t(block.fromLabel)}
              maxDate={today}
              showTimeInput
              required
            />
            <DateField
              name="to"
              id="cashback-report-block__to"
              title={t(block.toLabel)}
              maxDate={today}
              showTimeInput
              required
            />
            <SubmitButton
              value={t(block.exportCashback)}
              disabled={isGenerating}
            />
          </Form>
        </div>
      </CardBody>
    </Card>
  );
};

export default CashbackReportBlock;
